/**
 * The timeline dock — the bottom half of the clips screen.
 *
 * Collapsed it is your edit at a glance: one block per picked clip, width proportional to its
 * length, in play order. Drag a block sideways to re-order. Tap one to open the trim editor,
 * where two fat handles set exactly where the clip starts and ends and a live frame follows
 * your finger, so you trim by looking at the picture rather than at numbers.
 *
 * Times are SECONDS inside each clip's own source video (same convention as the web ET's
 * timeline dock), and every clip carries its own `video`, so an edit can span several files.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { M } from "./mobileStrings";
import { buzz } from "./native";
import { MIN_CLIP, clampTrim, fmtClock, fmtSpan, tapTime, totalPickedSeconds, trimWindow } from "./mobileModel";

const EPS = 0.03;          // stop a hair before the cut so we never bleed into the next clip
const MIN_BLOCK = 34;      // px — a 2s block must still be tappable
const STEP = 0.1;          // seconds per nudge, matching the website's editor
const PX_PER_SEC = 13;

/* ══════════ the edit player: plays every pick back to back, switching file per pick ══════════ */
function EditPlayer({ picks, onIndex, onDone }) {
  const vref = useRef(null);
  const [idx, setIdx] = useState(0);
  const picksRef = useRef(picks); picksRef.current = picks;
  const cb = useRef({ onIndex, onDone }); cb.current = { onIndex, onDone };
  const key = picks.map((p) => `${p.id}:${p.start}:${p.end}`).join("|");

  useEffect(() => { setIdx(0); }, [key]);

  useEffect(() => {
    const v = vref.current;
    const p = picksRef.current[idx];
    if (!v || !p) { cb.current.onDone(); return undefined; }
    cb.current.onIndex(idx);
    let raf = 0, cancelled = false;
    const tick = () => {
      if (cancelled) return;
      if (v.currentTime >= p.end - EPS || v.ended) {
        if (idx + 1 < picksRef.current.length) setIdx(idx + 1);
        else { v.pause(); cb.current.onDone(); }
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    const start = () => {
      if (cancelled) return;
      try { v.currentTime = p.start; } catch (e) { /* not seekable yet */ }
      const pr = v.play();
      if (pr && pr.catch) pr.catch(() => {});
      raf = requestAnimationFrame(tick);
    };
    if (v.getAttribute("data-src") !== p.video) {
      v.setAttribute("data-src", p.video);
      v.src = p.video;
      v.addEventListener("loadedmetadata", start, { once: true });
    } else start();
    return () => { cancelled = true; cancelAnimationFrame(raf); v.removeEventListener("loadedmetadata", start); };
  }, [idx, key]);

  return (
    <div className="ccm-stage">
      <video id="v-edit" ref={vref} playsInline preload="auto" />
    </div>
  );
}

/* ══════════ trim editor: handles reach outside the cut, and you can hear it ══════════ */
function TrimEditor({ pick, row, onChange, onClose, onRemove, onReset }) {
  const barRef = useRef(null);
  const vref = useRef(null);
  const [drag, setDrag] = useState(null);              // "start" | "end" | null
  const [local, setLocal] = useState({ start: pick.start, end: pick.end });
  const [playing, setPlaying] = useState(false);
  // Where the video is right now, in seconds on the bar. Isaac's ask: "there is no playhead to
  // tell me where it's at" — so every seek and every played frame moves this line.
  const [head, setHead] = useState(pick.start);
  const startAt = useRef(null);   // a tapped-in start for the NEXT play, instead of the clip start
  const win = useMemo(() => trimWindow(row), [row]);
  const span = Math.max(0.001, win.hi - win.lo);

  useEffect(() => { setLocal({ start: pick.start, end: pick.end }); setHead(pick.start); }, [pick.id, pick.start, pick.end]);

  const seek = useCallback((t) => {
    const v = vref.current;
    if (!v) return;
    const c = Math.min(Math.max(t, win.lo), win.hi);
    try { v.currentTime = c; } catch (e) { /* ignore */ }
    setHead(c);
  }, [win.lo, win.hi]);

  /** Hear exactly what you have chosen, which is the only way to know a trim is right. */
  useEffect(() => {
    if (!playing) { const v = vref.current; if (v) v.pause(); return undefined; }
    const v = vref.current;
    if (!v) return undefined;
    let raf = 0, cancelled = false;
    const tick = () => {
      if (cancelled) return;
      setHead(v.currentTime);
      if (v.currentTime >= local.end - EPS || v.ended) { v.pause(); setPlaying(false); setHead(local.start); return; }
      raf = requestAnimationFrame(tick);
    };
    const from = startAt.current != null ? startAt.current : local.start;
    startAt.current = null;
    try { v.currentTime = from; } catch (e) { /* ignore */ }
    v.muted = false;
    const pr = v.play();
    if (pr && pr.catch) pr.catch(() => setPlaying(false));
    raf = requestAnimationFrame(tick);
    return () => { cancelled = true; cancelAnimationFrame(raf); };
  }, [playing, local.start, local.end]);

  const timeAt = (clientX) => {
    const el = barRef.current;
    if (!el) return win.lo;
    const r = el.getBoundingClientRect();
    const f = Math.min(Math.max((clientX - r.left) / Math.max(1, r.width), 0), 1);
    return win.lo + f * span;
  };

  const onDown = (which) => (e) => {
    e.preventDefault(); e.stopPropagation();
    setPlaying(false); setDrag(which); buzz();
    if (e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = (e) => {
    if (!drag) return;
    const t = timeAt(e.clientX);
    setLocal((cur) => {
      const next = drag === "start"
        ? { start: Math.min(t, cur.end - MIN_CLIP), end: cur.end }
        : { start: cur.start, end: Math.max(t, cur.start + MIN_CLIP) };
      const c = clampTrim(row, next);
      seek(drag === "start" ? c.start : c.end);
      return c;
    });
  };
  const onUp = () => {
    if (!drag) return;
    setDrag(null); buzz(12);
    onChange(clampTrim(row, local));
  };

  /** A tap inside the kept range jumps the playhead there. Outside it, the handles own the bar. */
  const onBarDown = (e) => {
    if (drag || e.target !== e.currentTarget) return;   // grips and overlays handle their own taps
    const el = barRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const t = tapTime((e.clientX - r.left) / Math.max(1, r.width), win, local);
    if (t == null) return;
    buzz(6);
    const v = vref.current;
    if (playing && v) {
      try { v.currentTime = t; } catch (err) { /* ignore */ }
      setHead(t);
    } else {
      startAt.current = t;
      seek(t);
      setPlaying(true);
    }
  };

  const nudge = (which, delta) => {
    setPlaying(false);
    setLocal((cur) => {
      const next = which === "start"
        ? { start: cur.start + delta, end: cur.end }
        : { start: cur.start, end: cur.end + delta };
      const c = clampTrim(row, next);
      seek(which === "start" ? c.start : c.end);
      onChange(c);
      buzz(6);
      return c;
    });
  };

  const pct = (t) => `${(((t - win.lo) / span) * 100).toFixed(3)}%`;
  const dur = local.end - local.start;
  const changed = Math.abs(local.start - row.start_in) > 0.01 || Math.abs(local.end - row.end_in) > 0.01;
  /** Back to exactly what the clip was cut as, in one tap — Isaac wanted this here, not just in the list. */
  const reset = () => {
    setPlaying(false);
    setLocal({ start: row.start_in, end: row.end_in });
    seek(row.start_in);
    buzz(10);
    onReset();
  };

  return (
    <div className="ccm-trim">
      <div className="ccm-trim-head">
        <span className="n">{pick.order}</span>
        <span className="t">{playing ? `${fmtClock(head)} · ${fmtSpan(local.start, local.end)}` : fmtSpan(local.start, local.end)}</span>
        <span className="d">{dur.toFixed(1)}s</span>
        <button type="button" className="ccm-x" onClick={onRemove} aria-label={M.removeClip}>🗑</button>
        <button type="button" className="ccm-x" onClick={onClose} aria-label={M.done}>✕</button>
      </div>

      <div className="ccm-trim-stage">
        <video ref={vref} src={pick.video} playsInline preload="auto"
               onLoadedMetadata={() => seek(local.start)} />
        <button
          type="button"
          className={`ccm-trim-play${playing ? " is-on" : ""}`}
          onClick={() => { buzz(); setPlaying((v) => !v); }}
          aria-label={playing ? M.stop : M.hearIt}
        >
          {playing ? "❚❚" : "▶"}
        </button>
      </div>

      <div
        className={`ccm-trim-bar${drag ? " is-dragging" : ""}`}
        ref={barRef}
        onPointerDown={onBarDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      >
        {/* where the clip was originally cut, so it is obvious you are reaching past it */}
        <span className="cut-marks" style={{ left: pct(row.start_in), right: `${100 - parseFloat(pct(row.end_in))}%` }} />
        <div className="keep" style={{ left: pct(local.start), right: `${100 - parseFloat(pct(local.end))}%` }} />
        {/* the playhead: where the video is, moving while it plays, where you tapped when it isn't */}
        <span className={`head${playing ? " is-live" : ""}`} style={{ left: pct(head) }} aria-hidden="true" />
        <button type="button" className="grip grip--l" style={{ left: pct(local.start) }}
                onPointerDown={onDown("start")} aria-label={M.trimStart} />
        <button type="button" className="grip grip--r" style={{ left: pct(local.end) }}
                onPointerDown={onDown("end")} aria-label={M.trimEnd} />
      </div>

      {/* The website's 0.1s nudges, for when a finger is not precise enough — and, between them,
          the way back to the original cut. */}
      <div className="ccm-nudges">
        <div className="ccm-step">
          <span className="lbl">{M.nudgeStart}</span>
          <button type="button" onClick={() => nudge("start", -STEP)} aria-label="Start earlier">−</button>
          <span className="val">{local.start.toFixed(1)}s</span>
          <button type="button" onClick={() => nudge("start", STEP)} aria-label="Start later">+</button>
        </div>

        <div className="ccm-step">
          <span className="lbl">{M.nudgeEnd}</span>
          <button type="button" onClick={() => nudge("end", -STEP)} aria-label="End earlier">−</button>
          <span className="val">{local.end.toFixed(1)}s</span>
          <button type="button" onClick={() => nudge("end", STEP)} aria-label="End later">+</button>
        </div>
      </div>

      {/* One symmetric row under the nudges: the way back, only when there is somewhere to go. */}
      <button
        type="button" className={`ccm-pill ccm-pill--ghost ccm-pill--reset${changed ? "" : " is-off"}`}
        disabled={!changed} onClick={reset} aria-label={M.untrim}
      >
        <span aria-hidden="true">↺</span> {M.untrim}
      </button>

      <p className="ccm-trim-hint">{M.trimHint}</p>
    </div>
  );
}

/* ══════════ the dock ══════════ */
export default function MobileTimeline({
  picks, rows, playing, nowIndex, onPlay, onStop, onNowIndex,
  onTrim, onResetTrim, onRemove, onReorder, onShare, sharing, shareError,
}) {
  const [openId, setOpenId] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const stripRef = useRef(null);
  // A press-and-hold starts a re-order; a quick swipe still scrolls the strip and a quick tap
  // still opens the trim editor. Without the hold, every attempt to scroll would reorder, and
  // every reorder would also fire the tap and open the editor on top of it.
  const holdRef = useRef({ timer: 0, x: 0, moved: false, dragged: false });
  const total = totalPickedSeconds(picks);
  const rowById = useMemo(() => new Map((rows || []).map((r) => [String(r.id), r])), [rows]);

  const openPick = picks.find((p) => String(p.id) === String(openId));
  const openRow = openPick ? rowById.get(String(openPick.id)) : null;
  useEffect(() => { if (openId && !openPick) setOpenId(null); }, [openId, openPick]);

  const widthOf = (p) => Math.max(MIN_BLOCK, Math.round(p.sec * PX_PER_SEC));

  /* press and hold a block, then slide it, to re-order */
  const HOLD_MS = 180;
  const SLOP = 10; // px of movement still counted as a press rather than a swipe

  const startDrag = (i) => (e) => {
    if (picks.length < 2) return;
    const h = holdRef.current;
    h.x = e.clientX; h.moved = false; h.dragged = false;
    const target = e.currentTarget;
    const pointerId = e.pointerId;
    clearTimeout(h.timer);
    h.timer = setTimeout(() => {
      if (holdRef.current.moved) return;         // they were scrolling, not holding
      holdRef.current.dragged = true;
      setDragIdx(i);
      buzz(14);
      try { target.setPointerCapture(pointerId); } catch (err) { /* not fatal */ }
    }, HOLD_MS);
  };

  const moveDrag = (e) => {
    const h = holdRef.current;
    if (dragIdx == null) {
      if (h.timer && Math.abs(e.clientX - h.x) > SLOP) { h.moved = true; clearTimeout(h.timer); h.timer = 0; }
      return;
    }
    const el = stripRef.current;
    if (!el) return;
    const over = [...el.querySelectorAll("[data-blk]")].findIndex((k) => {
      const r = k.getBoundingClientRect();
      return e.clientX >= r.left && e.clientX <= r.right;
    });
    if (over >= 0 && over !== dragIdx) { onReorder(dragIdx, over); setDragIdx(over); buzz(6); }
  };

  const endDrag = () => {
    const h = holdRef.current;
    clearTimeout(h.timer); h.timer = 0;
    if (dragIdx != null) { setDragIdx(null); buzz(12); }
  };

  /** Only a real tap opens the trim editor — a finished drag must not also open it. */
  const tapBlock = (p) => () => {
    if (holdRef.current.dragged) { holdRef.current.dragged = false; return; }
    setOpenId(String(p.id) === String(openId) ? null : p.id);
    buzz();
  };

  if (!picks.length) {
    return (
      <div className="ccm-dock ccm-dock--empty">
        <p className="ccm-dock-hint">{M.tapToWatch}</p>
      </div>
    );
  }

  return (
    <div className="ccm-dock">
      {playing ? <EditPlayer picks={picks} onIndex={onNowIndex} onDone={onStop} /> : null}

      {openPick && openRow ? (
        <TrimEditor
          pick={openPick}
          row={openRow}
          onChange={(t) => onTrim(openPick.id, t)}
          onReset={() => onResetTrim(openPick.id)}
          onClose={() => setOpenId(null)}
          onRemove={() => { onRemove(openPick.id); setOpenId(null); }}
        />
      ) : null}

      <div className="ccm-dock-row">
        <div className="ccm-strip" ref={stripRef} onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={endDrag}>
          {picks.map((p, i) => (
            <button
              type="button"
              key={p.id}
              data-blk
              className={`ccm-blk${playing && i === nowIndex ? " is-now" : ""}${String(p.id) === String(openId) ? " is-open" : ""}${dragIdx === i ? " is-drag" : ""}`}
              style={{ width: widthOf(p) }}
              onPointerDown={startDrag(i)}
              onClick={tapBlock(p)}
              aria-label={`${M.clipN(p.order)} ${fmtSpan(p.start, p.end)}`}
            >
              {p.thumb ? <img src={p.thumb} alt="" draggable={false} /> : null}
              <i className="n">{p.order}</i>
              {p.trimmed ? <i className="cut" aria-hidden="true">✂</i> : null}
            </button>
          ))}
        </div>
        <div className="ccm-dock-total">
          <b>{fmtClock(total)}</b>
          <small>{M.picked(picks.length)}</small>
        </div>
      </div>

      {shareError ? <div className="ccm-error ccm-error--slim" role="alert">{shareError}</div> : null}

      <div className="ccm-dock-actions">
        {playing ? (
          <button type="button" className="ccm-pill ccm-pill--ghost" onClick={onStop}>{M.stop}</button>
        ) : (
          <button type="button" className="ccm-pill ccm-pill--ghost" onClick={onPlay}>▶ {M.playEdit}</button>
        )}
        <button type="button" className="ccm-pill" onClick={onShare} disabled={sharing}>
          {sharing ? M.making : M.share}
        </button>
      </div>
    </div>
  );
}
